import { useDraft } from '@cascade/react/hooks';
import { useI18n } from '@cascade/react/i18n';
import {
  areTagGroupsEqual,
  hasRequiredTagValues,
  useEmailTagsMutation,
  useProjects,
} from '@cascade/react/projects';
import { Button, Select, Text } from '@cascade/ui-library';
import {
  toDefaultTagValues,
  toTagItems,
  toTagOptions,
} from './TagForm.helpers.ts';
import classes from './TagForm.module.scss';
import { SupportContact } from '#components/support-contact/index.ts';

export const TagForm = () => {
  const { translate, translateError } = useI18n();

  const { tags, tagValues, editableTags, hasLockedRequiredTags, canEditTags } =
    useProjects();

  const { isSettingEmailTags, setEmailTagsError, setEmailTags } =
    useEmailTagsMutation();

  const defaultValues = toDefaultTagValues(editableTags);
  const initialValues = { ...tagValues, ...defaultValues };

  const {
    draft: values,
    isDirty,
    setDraft: setValues,
  } = useDraft(tagValues, {
    initial: initialValues,
    isEqual: areTagGroupsEqual,
  });

  const isComplete = hasRequiredTagValues(editableTags, values);
  const canSave = isComplete && isDirty && !isSettingEmailTags;

  const setTagValue = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const save = () => {
    setEmailTags({ tags: toTagItems(editableTags, values) }).catch(
      () => undefined,
    );
  };

  return (
    <div className={classes['tag-form']}>
      {tags.map((tag) => (
        <Select
          key={tag.key}
          label={tag.display_name}
          placeholder={translate('tags.form.placeholder', {
            tag: tag.display_name,
          })}
          options={toTagOptions(tag)}
          value={values[tag.key] ?? tagValues[tag.key]}
          required={tag.required}
          disabled={!editableTags.includes(tag) || isSettingEmailTags}
          onChange={(value: string) => setTagValue(tag.key, value)}
        />
      ))}
      {hasLockedRequiredTags ? (
        <>
          <Text
            type='body-2'
            role='alert'
            content={translate('tags.form.locked')}
          />
          <SupportContact />
        </>
      ) : null}
      {setEmailTagsError ? (
        <Text
          type='body-2'
          color='text-danger'
          role='alert'
          content={translateError(setEmailTagsError)}
        />
      ) : null}
      {canEditTags ? (
        <Button
          label={translate('tags.form.save')}
          disabled={!canSave}
          onClick={save}
        />
      ) : null}
    </div>
  );
};
